/* =====================================================================
   TAB Calculator Library — BEER (Brewing) data / config  ·  V2
   Ported verbatim from the approved prototype (calc-data.js: `beer`,
   GROUPS.beer, META). 12 calculators only, in three groups:
     • Plan your batch      • Measure and adjust     • Carbonate and package
   Formulas, fields, help text, groups and metadata are the approved V2.
   Loaded only on the Beer page; registers with the shared engine.
   ===================================================================== */
(function () {
  'use strict';

  var num = function (x) { return parseFloat(x); };
  var has = function (x) { return x !== '' && x != null && !isNaN(parseFloat(x)); };
  var d = function (n, dp) { return Number(n).toFixed(dp); };
  var sgToBrix = function (sg) { return (((182.4601 * sg - 775.6821) * sg + 1262.7794) * sg - 669.5622); };

  var beer = [
    {
      id: 'beer-abv',
      name: 'ABV Calculator',
      desc: 'Calculate alcohol percentage from Original Gravity and Final Gravity.',
      group: 'plan', tag: 'Start here',
      need: 'Your hydrometer readings before and after fermentation.',
      accuracy: 'This is an estimate. Actual alcohol content may vary.',
      next: [{ t: 'beer-priming', label: 'Calculate priming sugar' }, { h: '/blogs/all/what-is-original-gravity-in-beer-brewing', label: 'What is Original Gravity?' }],
      fields: [
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001', help: 'Your hydrometer reading before fermentation begins.' },
        { key: 'fg', label: 'Final Gravity', type: 'number', unit: 'SG', default: '1.010', step: '0.001', help: 'Your hydrometer reading once fermentation is complete.' },
      ],
      compute: function (v, s) {
        if (!has(v.og) || !has(v.fg)) return null;
        var og = num(v.og), fg = num(v.fg);
        if (og < 0.9 || og > 1.25 || fg < 0.9 || fg > 1.25) return { error: 'Enter a valid gravity reading.' };
        if (fg > og) return { error: 'Final gravity should be lower than original gravity.' };
        var abv = (og - fg) * 131.25;
        return { main: { value: d(abv, 1), unit: '% ABV', label: 'Alcohol by volume' },
          note: 'Your beer is about ' + d(abv, 1) + '% alcohol by volume.' };
      },
    },
    {
      id: 'beer-priming',
      name: 'Priming Sugar Calculator',
      desc: 'Calculate the sugar needed to carbonate bottled beer.',
      group: 'package', tag: 'Most used',
      guide: 'Carbonation is measured in “volumes of CO₂”. Most ales sit around 2.2–2.6, wheat beers higher. Use the temperature the beer has been sitting at.',
      need: 'Your final batch volume, beer temperature and desired carbonation level.',
      accuracy: 'For best accuracy, use a calibrated thermometer and hydrometer.',
      next: [{ t: 'beer-bottle', label: 'Check bottle yield' }, { h: '/blogs/all/understanding-carbonation-levels-in-beer', label: 'Understanding carbonation levels' }],
      fields: [
        { key: 'vol', label: 'Batch volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'temp', label: 'Beer temperature', type: 'number', dim: 'temp', default: '20', help: 'Warmest temp the beer has reached.' },
        { key: 'vols', label: 'Desired carbonation level', type: 'number', unit: 'vols CO₂', default: '2.4', step: '0.1', help: 'Ales 2.2–2.6 · Lagers 2.4–2.8 · Wheat 3.0+' },
        { key: 'sugar', label: 'Priming sugar type', type: 'select', default: 'dextrose',
          options: [{ value: 'dextrose', label: 'Corn sugar (dextrose)' }, { value: 'sucrose', label: 'Table sugar (sucrose)' }, { value: 'dme', label: 'Dry malt extract (DME)' }] },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.temp) || !has(v.vols)) return null;
        var volL = s.toL(v.vol), tF = s.toC(v.temp) * 9 / 5 + 32, vols = num(v.vols);
        var residual = 3.0378 - 0.050062 * tF + 0.00026555 * tF * tF;
        if (vols < residual) return { error: 'Desired level is below the CO₂ already in the beer.' };
        var fac = { dextrose: 1, sucrose: 0.91, dme: 1.7 }[v.sugar];
        // 15.195 g is per US gallon per volume of CO2 (dextrose) — convert litres to gallons.
        var volGal = volL / 3.785411784;
        var g = 15.195 * volGal * (vols - residual) * fac;
        var w = s.wt(g);
        return { main: { value: w.value, unit: w.unit, label: 'Priming sugar to add' },
          note: 'Dissolve in warm water and mix into the whole batch before bottling.' };
      },
    },
    {
      id: 'beer-keg',
      name: 'Keg Carbonation / CO₂ Calculator',
      desc: 'Calculate serving pressure and carbonation for kegged beer.',
      group: 'package',
      need: 'Your serving temperature and desired carbonation level.',
      accuracy: 'An estimate — leave the keg to reach equilibrium, then fine-tune to taste.',
      next: [{ h: '/blogs/all/understanding-carbonation-levels-in-beer', label: 'Understanding carbonation levels' }],
      fields: [
        { key: 'temp', label: 'Beer temperature', type: 'number', dim: 'temp', default: '4', help: 'Serving fridge temperature.' },
        { key: 'vols', label: 'Desired carbonation level', type: 'number', unit: 'vols CO₂', default: '2.4', step: '0.1' },
        { key: 'gas', label: 'Gas type', type: 'select', default: 'co2',
          options: [{ value: 'co2', label: 'CO₂' }, { value: 'mixed', label: 'Mixed gas (beer gas)' }] },
        { key: 'unit', label: 'Keg pressure unit', type: 'select', default: 'psi',
          options: [{ value: 'psi', label: 'PSI' }, { value: 'kpa', label: 'kPa' }, { value: 'bar', label: 'bar' }] },
      ],
      compute: function (v, s) {
        if (!has(v.temp) || !has(v.vols)) return null;
        var tF = s.toC(v.temp) * 9 / 5 + 32, V = num(v.vols);
        var psi = -16.6999 - 0.0101059 * tF + 0.00116512 * tF * tF + 0.173354 * tF * V + 4.24267 * V - 0.0684226 * V * V;
        if (psi < 0) psi = 0;
        var conv = { psi: [psi, 'PSI'], kpa: [psi * 6.89476, 'kPa'], bar: [psi * 0.0689476, 'bar'] }[v.unit];
        var note = v.gas === 'mixed'
          ? 'Beer gas blends dispense at higher set pressure — this is the equivalent CO₂ pressure.'
          : 'Set your regulator to this pressure and leave the keg to reach equilibrium.';
        return { main: { value: d(conv[0], v.unit === 'bar' ? 2 : 1), unit: conv[1], label: 'Recommended serving pressure' }, note: note };
      },
    },
    {
      id: 'beer-hydro',
      name: 'Hydrometer Temperature Correction',
      desc: 'Correct gravity readings taken at a temperature other than calibration.',
      group: 'measure',
      need: 'Your measured gravity, sample temperature and the hydrometer’s calibration temperature.',
      accuracy: 'For best accuracy, use a calibrated hydrometer.',
      next: [{ t: 'beer-abv', label: 'Calculate ABV' }, { h: '/blogs/all/how-to-take-a-hydrometer-reading-for-home-brewing', label: 'How to take a hydrometer reading' }],
      fields: [
        { key: 'sg', label: 'Measured gravity', type: 'number', unit: 'SG', default: '1.045', step: '0.001' },
        { key: 'temp', label: 'Sample temperature', type: 'number', dim: 'temp', default: '30' },
        { key: 'cal', label: 'Hydrometer calibration temperature', type: 'number', dim: 'temp', default: '20', help: 'Printed on the hydrometer — usually 20 °C.' },
      ],
      compute: function (v, s) {
        if (!has(v.sg) || !has(v.temp) || !has(v.cal)) return null;
        var f = function (c) { var t = c * 9 / 5 + 32; return 1.00130346 - 1.34722124e-4 * t + 2.04052596e-6 * t * t - 2.32820948e-9 * t * t * t; };
        var cg = num(v.sg) * f(s.toC(v.temp)) / f(s.toC(v.cal));
        return { main: { value: d(cg, 3), unit: 'SG', label: 'Corrected gravity reading' },
          note: 'Use this corrected figure for your ABV and other calculations.' };
      },
    },
    {
      id: 'beer-refract',
      name: 'Refractometer Correction Calculator',
      desc: 'Correct refractometer readings after fermentation.',
      group: 'measure', tag: 'Advanced',
      guide: 'Alcohol skews refractometer readings once fermentation starts. Enter your original gravity and the current reading in Brix to recover an accurate final gravity.',
      need: 'Your original gravity and current refractometer reading in Brix.',
      accuracy: 'Refractometer corrections are approximate — confirm with a hydrometer where you can.',
      next: [{ t: 'beer-abv', label: 'Calculate ABV' }, { h: '/blogs/all/how-to-read-a-refractometer-when-brewing-beer', label: 'Reading a refractometer' }],
      fields: [
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'cur', label: 'Current refractometer reading', type: 'number', unit: '°Bx', default: '8', step: '0.1' },
      ],
      compute: function (v, s) {
        if (!has(v.og) || !has(v.cur)) return null;
        var wcf = 1.04;
        var ri = sgToBrix(num(v.og)) / wcf, rf = num(v.cur) / wcf;
        var fg = 1.0 - 0.0044993 * ri + 0.011774 * rf + 0.00027581 * ri * ri - 0.0012717 * rf * rf - 0.0000072800 * ri * ri * ri + 0.0000063293 * rf * rf * rf;
        var abv = (num(v.og) - fg) * 131.25;
        return { main: { value: d(fg, 3), unit: 'SG', label: 'Corrected Final Gravity' },
          extra: [{ value: d(abv, 1), unit: '% ABV', label: 'Estimated ABV' }],
          note: 'Corrected for the alcohol present in the fermented sample.' };
      },
    },
    {
      id: 'beer-ibu',
      name: 'IBU / Hop Bitterness Calculator',
      desc: 'Estimate bitterness from hop additions (Tinseth method).',
      group: 'measure',
      need: 'Your batch volume, gravity, hop weight, alpha acid % and boil time.',
      accuracy: 'Bitterness figures are estimates and vary with hop age and boil vigour.',
      next: [{ h: '/blogs/all/understanding-ibu-and-bitterness-in-beer', label: 'Understanding IBU and bitterness' }],
      fields: [
        { key: 'vol', label: 'Batch volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'wt', label: 'Hop weight', type: 'number', unit: 'g', default: '30' },
        { key: 'aa', label: 'Alpha acid percentage', type: 'number', unit: '%', default: '6', step: '0.1', help: 'The alpha acid % printed on your hop packet.' },
        { key: 'time', label: 'Boil time', type: 'number', unit: 'min', default: '60' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.og) || !has(v.wt) || !has(v.aa) || !has(v.time)) return null;
        var volL = s.toL(v.vol), og = num(v.og);
        var util = (1.65 * Math.pow(0.000125, og - 1)) * ((1 - Math.exp(-0.04 * num(v.time))) / 4.15);
        var ibu = (num(v.aa) / 100 * num(v.wt) * 1000) / volL * util;
        return { main: { value: d(ibu, 0), unit: 'IBU', label: 'Estimated bitterness' },
          note: 'For one hop addition of this size. Sum additions for total bitterness.' };
      },
    },
    {
      id: 'beer-colour',
      name: 'Beer Colour Calculator',
      desc: 'Estimate colour in EBC and SRM (Morey method).',
      group: 'measure',
      need: 'Your batch volume, grain or extract weight and its colour rating.',
      accuracy: 'A single-grain estimate — colour varies with process and ingredients.',
      next: [{ h: '/collections/beer-brewing', label: 'Browse beer brewing guides' }],
      fields: [
        { key: 'vol', label: 'Batch volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'wt', label: 'Grain or extract weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'color', label: 'Colour rating', type: 'number', unit: '°L', default: '6', step: '0.5', help: 'Lovibond rating of the grain or extract.' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.wt) || !has(v.color)) return null;
        var volGal = s.toL(v.vol) / 3.785411784, lb = num(v.wt) * 2.2046226;
        var mcu = (lb * num(v.color)) / volGal;
        var srm = 1.4922 * Math.pow(mcu, 0.6859);
        return { main: { value: d(srm * 1.97, 1), unit: 'EBC', label: 'Estimated colour (EBC)' },
          extra: [{ value: d(srm, 1), unit: 'SRM', label: 'Estimated SRM' }],
          note: 'A single-grain estimate. Sum contributions for a full grain bill.' };
      },
    },
    {
      id: 'beer-yeast',
      name: 'Yeast Pitching Rate Calculator',
      desc: 'Estimate yeast required for ales and lagers.',
      group: 'plan',
      need: 'Your batch volume, original gravity and yeast format.',
      accuracy: 'Cell counts are estimates — check your yeast’s viability and date.',
      next: [{ h: '/blogs/all/pitching-yeast-how-much-yeast-do-you-need-for-beer', label: 'Pitching yeast: how much you need' }],
      fields: [
        { key: 'vol', label: 'Batch volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'type', label: 'Beer type', type: 'select', default: 'ale',
          options: [{ value: 'ale', label: 'Ale' }, { value: 'lager', label: 'Lager' }] },
        { key: 'format', label: 'Yeast format', type: 'select', default: 'dry',
          options: [{ value: 'dry', label: 'Dry' }, { value: 'liquid', label: 'Liquid' }] },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.og)) return null;
        var volL = s.toL(v.vol), og = num(v.og);
        var plato = -616.868 + 1111.14 * og - 630.272 * og * og + 135.997 * og * og * og;
        var rate = v.type === 'lager' ? 1.5 : 0.75; // million cells / mL / °P
        var billion = rate * (volL * 1000) * plato / 1000;
        if (v.format === 'dry') {
          var g = billion / 10; // ~10 B viable cells per gram
          var w = s.wt(g);
          return { main: { value: w.value, unit: w.unit, label: 'Dry yeast to pitch' },
            note: 'About ' + d(billion, 0) + ' billion cells. Rehydrate before pitching for best results.' };
        }
        var packs = billion / 100; // ~100 B cells per fresh pack
        return { main: { value: d(packs, 1), unit: packs >= 2 ? 'packs' : 'pack', label: 'Liquid yeast packs' },
          note: packs > 1.1 ? 'More than one pack needed — make a yeast starter to build cell count.' : 'A single fresh pack is enough for this batch.' };
      },
    },
    {
      id: 'beer-dilution',
      name: 'Dilution Calculator',
      desc: 'Calculate water required to reduce beer to a target ABV.',
      group: 'measure',
      need: 'Your current volume, current ABV and target ABV.',
      accuracy: 'This is an estimate. Actual alcohol content may vary.',
      next: [{ t: 'beer-abv', label: 'Calculate ABV' }],
      fields: [
        { key: 'vol', label: 'Current volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'cabv', label: 'Current ABV', type: 'number', unit: '%', default: '6', step: '0.1' },
        { key: 'tabv', label: 'Target ABV', type: 'number', unit: '%', default: '4.5', step: '0.1' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.cabv) || !has(v.tabv)) return null;
        if (num(v.vol) <= 0) return { error: 'Current volume must be greater than zero.' };
        if (num(v.tabv) >= num(v.cabv)) return { error: 'Target ABV must be lower than current ABV.' };
        var curL = s.toL(v.vol), waterL = curL * (num(v.cabv) / num(v.tabv)) - curL;
        var w = s.vol(waterL), f = s.vol(curL + waterL);
        return { main: { value: w.value, unit: w.unit, label: 'Water to add' },
          extra: [{ value: f.value, unit: f.unit, label: 'Final volume' }],
          note: 'Adds water only — dilutes flavour and body along with alcohol.' };
      },
    },
    {
      id: 'beer-bottle',
      name: 'Bottle Yield Calculator',
      desc: 'Calculate bottle quantity from batch volume.',
      group: 'package', tag: 'Packaging',
      need: 'Your total packaged volume and chosen bottle size.',
      next: [{ t: 'beer-priming', label: 'Calculate priming sugar' }, { h: '/blogs/all/how-to-bottle-beer-at-home', label: 'How to bottle beer' }],
      fields: [
        { key: 'vol', label: 'Total packaged volume', type: 'number', dim: 'vol', default: '19' },
        { key: 'size', label: 'Bottle size', type: 'select', default: '375',
          options: [{ value: '330', label: '330 mL' }, { value: '375', label: '375 mL' }, { value: '500', label: '500 mL' }, { value: '750', label: '750 mL' }] },
      ],
      compute: function (v, s) {
        if (!has(v.vol)) return null;
        if (num(v.vol) <= 0) return { error: 'Total packaged volume must be greater than zero.' };
        if (num(v.size) <= 0) return { error: 'Bottle size must be greater than zero.' };
        var mL = s.toL(v.vol) * 1000, bottles = Math.ceil(mL / num(v.size));
        return { main: { value: String(bottles), unit: 'bottles', label: 'Bottles required' },
          extra: [{ value: String(bottles), unit: 'caps', label: 'Caps required' }],
          note: 'Rounded up — keep a few spare bottles and caps on hand.' };
      },
    },
    {
      id: 'beer-water',
      name: 'Batch Water Calculator',
      desc: 'Estimate brewing water requirements.',
      group: 'plan',
      need: 'Your target batch volume, boil time, boil-off rate and equipment losses.',
      accuracy: 'An estimate — measure your own boil-off rate for best results.',
      next: [{ h: '/collections/beer-brewing', label: 'Browse beer brewing guides' }],
      fields: [
        { key: 'vol', label: 'Target batch volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'time', label: 'Boil time', type: 'number', unit: 'min', default: '60' },
        { key: 'boiloff', label: 'Boil-off rate', type: 'number', dim: 'rate', default: '3', help: 'Water lost to steam each hour.' },
        { key: 'trub', label: 'Trub & equipment loss', type: 'number', dim: 'vol', default: '2', help: 'Left behind in kettle and lines.' },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.time) || !has(v.boiloff) || !has(v.trub)) return null;
        var batchL = s.toL(v.vol), boilL = s.toL(v.boiloff) * (num(v.time) / 60), trubL = s.toL(v.trub);
        var preBoil = batchL + trubL + boilL;
        var t = s.vol(preBoil), p = s.vol(preBoil);
        return { main: { value: t.value, unit: t.unit, label: 'Total water required' },
          extra: [{ value: p.value, unit: p.unit, label: 'Pre-boil volume' }],
          note: 'Add grain absorption separately if all-grain brewing.' };
      },
    },
    {
      id: 'beer-extract',
      name: 'Extract Conversion Calculator',
      desc: 'Convert between malt extract and grain equivalents.',
      group: 'plan',
      need: 'The ingredient you have, the amount, and the ingredient to convert to.',
      accuracy: 'Grain figures assume around 75% mash efficiency.',
      next: [{ h: '/collections/beer-brewing', label: 'Browse beer brewing guides' }],
      fields: [
        { key: 'from', label: 'Ingredient type', type: 'select', default: 'grain',
          options: [{ value: 'grain', label: 'Grain (all-grain)' }, { value: 'lme', label: 'Liquid malt extract' }, { value: 'dme', label: 'Dry malt extract' }] },
        { key: 'amount', label: 'Amount', type: 'number', unit: 'kg', default: '1', step: '0.1' },
        { key: 'to', label: 'Target ingredient type', type: 'select', default: 'dme',
          options: [{ value: 'grain', label: 'Grain (all-grain)' }, { value: 'lme', label: 'Liquid malt extract' }, { value: 'dme', label: 'Dry malt extract' }] },
      ],
      compute: function (v, s) {
        if (!has(v.amount)) return null;
        if (v.from === v.to) return { error: 'Choose two different ingredient types to convert.' };
        var fac = { grain: 0.62, lme: 0.82, dme: 1.0 }; // relative fermentable yield
        var kg = num(v.amount) * fac[v.from] / fac[v.to];
        var w = s.wt(kg * 1000);
        var lbl = { grain: 'grain', lme: 'liquid malt extract', dme: 'dry malt extract' }[v.to];
        return { main: { value: w.value, unit: w.unit, label: 'Equivalent ' + lbl },
          note: 'Grain figures assume around 75% mash efficiency.' };
      },
    },

    /* ---------- Mash & all-grain ---------- */
    {
      id: 'beer-strike',
      name: 'Strike Water Temperature Calculator',
      desc: 'Calculate the water temperature needed to hit your target mash temperature.',
      group: 'mash', tag: 'All-grain',
      need: 'Your grain weight, mash water volume, grain temperature and target mash temperature.',
      accuracy: 'Assumes typical grain heat capacity — heat your mash tun first for best accuracy.',
      next: [{ t: 'beer-restinf', label: 'Raise the mash temperature' }, { h: '/collections/all-grain-brewing-systems', label: 'Shop all-grain systems' }],
      fields: [
        { key: 'grain', label: 'Grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'vol', label: 'Mash water volume', type: 'number', dim: 'vol', default: '15' },
        { key: 'grainT', label: 'Grain / ambient temperature', type: 'number', dim: 'temp', default: '20' },
        { key: 'target', label: 'Target mash temperature', type: 'number', dim: 'temp', default: '66' },
      ],
      compute: function (v, s) {
        if (!has(v.grain) || !has(v.vol) || !has(v.grainT) || !has(v.target)) return null;
        var G = num(v.grain), W = s.toL(v.vol), Tg = s.toC(v.grainT), Tt = s.toC(v.target);
        if (G <= 0 || W <= 0) return { error: 'Enter grain weight and water volume above zero.' };
        var strikeC = Tt + (0.4172 / (W / G)) * (Tt - Tg);
        var val = s.tempU === '°F' ? d(strikeC * 9 / 5 + 32, 1) : d(strikeC, 1);
        return { main: { value: val, unit: s.tempU, label: 'Strike water temperature' },
          note: 'Heat your mash water to this temperature before mixing in the grain.' };
      },
    },
    {
      id: 'beer-mashsparge',
      name: 'Mash & Sparge Water Calculator',
      desc: 'Calculate the mash and sparge water required for an all-grain brew.',
      group: 'mash',
      need: 'Your batch volume, grain weight, boil time, mash thickness and losses.',
      accuracy: 'An estimate — measure your own absorption and boil-off rates for best results.',
      next: [{ t: 'beer-grist', label: 'Check your grist ratio' }, { t: 'beer-strike', label: 'Calculate strike temperature' }],
      fields: [
        { key: 'batch', label: 'Batch volume (into fermenter)', type: 'number', dim: 'vol', default: '23' },
        { key: 'grain', label: 'Grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'thick', label: 'Mash thickness', type: 'number', unit: 'L/kg', default: '2.7', step: '0.1', help: 'Around 2.6–3.0 L/kg is typical.' },
        { key: 'boiloff', label: 'Boil-off rate', type: 'number', dim: 'rate', default: '3' },
        { key: 'time', label: 'Boil time', type: 'number', unit: 'min', default: '60' },
        { key: 'absorb', label: 'Grain absorption', type: 'number', unit: 'L/kg', default: '1.0', step: '0.1', help: 'Water retained by the spent grain.' },
        { key: 'loss', label: 'Kettle & deadspace loss', type: 'number', dim: 'vol', default: '1' },
      ],
      compute: function (v, s) {
        if (!has(v.batch) || !has(v.grain) || !has(v.thick)) return null;
        var batch = s.toL(v.batch), grain = num(v.grain), thick = num(v.thick);
        var boilOff = (has(v.boiloff) && has(v.time)) ? s.toL(v.boiloff) * (num(v.time) / 60) : 0;
        var absorb = (has(v.absorb) ? num(v.absorb) : 1) * grain;
        var loss = has(v.loss) ? s.toL(v.loss) : 0;
        var mash = grain * thick, total = batch + boilOff + absorb + loss, sparge = total - mash;
        if (sparge < 0) return { error: 'Mash water already exceeds the total — lower the mash thickness.' };
        var t = s.vol(total), m = s.vol(mash), sp = s.vol(sparge);
        return { main: { value: t.value, unit: t.unit, label: 'Total water required' },
          extra: [{ value: m.value, unit: m.unit, label: 'Mash water' }, { value: sp.value, unit: sp.unit, label: 'Sparge water' }],
          note: 'Split your water into the mash and sparge amounts shown.' };
      },
    },
    {
      id: 'beer-grist',
      name: 'Grist Ratio Calculator',
      desc: 'Calculate the grain-to-water ratio (mash thickness).',
      group: 'mash',
      need: 'Your grain weight and mash water volume.',
      next: [{ t: 'beer-mashsparge', label: 'Plan mash & sparge water' }],
      fields: [
        { key: 'grain', label: 'Grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'vol', label: 'Mash water volume', type: 'number', dim: 'vol', default: '13.5' },
      ],
      compute: function (v, s) {
        if (!has(v.grain) || !has(v.vol)) return null;
        if (num(v.grain) <= 0) return { error: 'Enter a grain weight above zero.' };
        var Lkg = s.toL(v.vol) / num(v.grain);
        return { main: { value: d(Lkg, 2), unit: 'L/kg', label: 'Mash thickness' },
          extra: [{ value: d(Lkg / 2.0864, 2), unit: 'qt/lb', label: 'Imperial ratio' }],
          note: 'Around 2.6–3.0 L/kg is a typical single-infusion mash.' };
      },
    },
    {
      id: 'beer-mashph',
      name: 'Mash pH Adjustment Calculator',
      desc: 'Estimate the acid needed to lower mash pH.',
      group: 'mash', tag: 'Advanced',
      need: 'Your mash water volume, water alkalinity, current pH, target pH and acid type.',
      accuracy: 'Mash pH also depends on your grain bill and water profile. Treat this as an approximate starting dose and confirm with a pH meter.',
      next: [{ t: 'beer-mashsparge', label: 'Plan mash & sparge water' }],
      fields: [
        { key: 'vol', label: 'Mash water volume', type: 'number', dim: 'vol', default: '15' },
        { key: 'alk', label: 'Water alkalinity (as CaCO₃)', type: 'number', unit: 'ppm', default: '100' },
        { key: 'cur', label: 'Current / estimated mash pH', type: 'number', unit: 'pH', default: '5.8', step: '0.1' },
        { key: 'target', label: 'Target mash pH', type: 'number', unit: 'pH', default: '5.4', step: '0.1' },
        { key: 'acid', label: 'Acid type', type: 'select', default: 'lactic',
          options: [{ value: 'lactic', label: 'Lactic acid (88%)' }, { value: 'phosphoric', label: 'Phosphoric acid (10%)' }] },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.alk) || !has(v.cur) || !has(v.target)) return null;
        if (num(v.target) >= num(v.cur)) return { error: 'Target pH must be below the current pH.' };
        var L = s.toL(v.vol);
        var frac = Math.min((num(v.cur) - num(v.target)) / 0.4, 2);
        var mEq = L * (num(v.alk) / 50) * frac;
        var perMl = { lactic: 11.82, phosphoric: 1.6 }[v.acid];
        var ml = Math.max(mEq / perMl, 0);
        return { main: { value: d(ml, 1), unit: 'mL', label: (v.acid === 'lactic' ? 'Lactic acid (88%)' : 'Phosphoric acid (10%)') + ' to add' },
          note: 'Add about half, stir, and measure before adding more.' };
      },
    },
    {
      id: 'beer-restinf',
      name: 'Rest & Infusion Temperature Calculator',
      desc: 'Calculate the boiling water to add to raise the mash to a new rest.',
      group: 'mash',
      need: 'Your grain weight, current mash water, current and target temperatures, and infusion water temperature.',
      accuracy: 'Assumes typical grain heat capacity — stir well and check with a thermometer.',
      next: [{ t: 'beer-decoction', label: 'Use a decoction instead' }],
      fields: [
        { key: 'grain', label: 'Grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'vol', label: 'Current mash water volume', type: 'number', dim: 'vol', default: '15' },
        { key: 'cur', label: 'Current mash temperature', type: 'number', dim: 'temp', default: '50' },
        { key: 'target', label: 'Target mash temperature', type: 'number', dim: 'temp', default: '66' },
        { key: 'infuse', label: 'Infusion water temperature', type: 'number', dim: 'temp', default: '100' },
      ],
      compute: function (v, s) {
        if (!has(v.grain) || !has(v.vol) || !has(v.cur) || !has(v.target) || !has(v.infuse)) return null;
        var Tc = s.toC(v.cur), Tt = s.toC(v.target), Ti = s.toC(v.infuse), W = s.toL(v.vol), G = num(v.grain);
        if (Tt <= Tc) return { error: 'Target temperature must be warmer than the current mash.' };
        if (Ti <= Tt) return { error: 'Infusion water must be hotter than the target temperature.' };
        var add = (Tt - Tc) * (0.41 * G + W) / (Ti - Tt);
        var a = s.vol(add), fv = s.vol(W + add);
        return { main: { value: a.value, unit: a.unit, label: 'Boiling water to add' },
          extra: [{ value: fv.value, unit: fv.unit, label: 'New mash water volume' }],
          note: 'Stir in gradually to reach the target rest.' };
      },
    },
    {
      id: 'beer-decoction',
      name: 'Decoction Volume Calculator',
      desc: 'Calculate the volume of mash to pull, boil and return for a decoction step.',
      group: 'mash', tag: 'Advanced',
      need: 'Your grain weight, mash water, decoction (boil) temperature, current and target rest temperatures.',
      accuracy: 'An estimate — pull a thick decoction and adjust to hit your target rest.',
      next: [{ t: 'beer-restinf', label: 'Infuse with water instead' }],
      fields: [
        { key: 'grain', label: 'Grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'vol', label: 'Mash water volume', type: 'number', dim: 'vol', default: '15' },
        { key: 'boil', label: 'Decoction temperature (boiling)', type: 'number', dim: 'temp', default: '100' },
        { key: 'target', label: 'Target rest temperature', type: 'number', dim: 'temp', default: '66' },
        { key: 'current', label: 'Current mash temperature', type: 'number', dim: 'temp', default: '52' },
      ],
      compute: function (v, s) {
        if (!has(v.grain) || !has(v.vol) || !has(v.boil) || !has(v.target) || !has(v.current)) return null;
        var Tb = s.toC(v.boil), Tt = s.toC(v.target), Tc = s.toC(v.current);
        if (Tt <= Tc) return { error: 'Target rest must be warmer than the current mash.' };
        if (Tb <= Tc) return { error: 'Decoction (boil) temperature must be warmer than the mash.' };
        var eff = s.toL(v.vol) + 0.41 * num(v.grain);
        var vd = eff * (Tt - Tc) / (Tb - Tc);
        var out = s.vol(vd);
        return { main: { value: out.value, unit: out.unit, label: 'Mash to pull for decoction' },
          note: 'Pull this volume of thick mash, boil, then stir back in.' };
      },
    },
    {
      id: 'beer-masheff',
      name: 'Mash Efficiency Calculator',
      desc: 'Calculate the sugar extracted from the grain into the kettle.',
      group: 'mash',
      need: 'Your pre-boil gravity and volume, grain weight and average grain potential.',
      accuracy: 'Grain potential varies by malt — 37 PPG is a typical base malt figure.',
      next: [{ t: 'beer-brewhouse', label: 'Check brewhouse efficiency' }],
      fields: [
        { key: 'pbg', label: 'Pre-boil gravity', type: 'number', unit: 'SG', default: '1.045', step: '0.001' },
        { key: 'pbvol', label: 'Pre-boil volume', type: 'number', dim: 'vol', default: '28' },
        { key: 'grain', label: 'Total grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'ppg', label: 'Average grain potential', type: 'number', unit: 'PPG', default: '37', help: 'Points per pound per gallon — base malt ≈ 37.' },
      ],
      compute: function (v, s) {
        if (!has(v.pbg) || !has(v.pbvol) || !has(v.grain) || !has(v.ppg)) return null;
        var pts = (num(v.pbg) - 1) * 1000, volGal = s.toL(v.pbvol) / 3.785411784, lb = num(v.grain) * 2.2046226;
        if (pts <= 0 || lb <= 0 || num(v.ppg) <= 0) return { error: 'Enter a gravity above 1.000 and a grain weight.' };
        var eff = (pts * volGal) / (lb * num(v.ppg)) * 100;
        return { main: { value: d(eff, 1), unit: '%', label: 'Mash efficiency' },
          note: 'Sugar extracted from the grain into the kettle.' };
      },
    },
    {
      id: 'beer-brewhouse',
      name: 'Brewhouse Efficiency Calculator',
      desc: 'Calculate the overall efficiency of your system, into the fermenter.',
      group: 'mash',
      need: 'Your original gravity and volume into the fermenter, grain weight and grain potential.',
      accuracy: 'Grain potential varies by malt — 37 PPG is a typical base malt figure.',
      next: [{ t: 'beer-masheff', label: 'Check mash efficiency' }],
      fields: [
        { key: 'og', label: 'Original gravity (into fermenter)', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'vol', label: 'Volume into fermenter', type: 'number', dim: 'vol', default: '23' },
        { key: 'grain', label: 'Total grain weight', type: 'number', unit: 'kg', default: '5', step: '0.1' },
        { key: 'ppg', label: 'Average grain potential', type: 'number', unit: 'PPG', default: '37', help: 'Points per pound per gallon — base malt ≈ 37.' },
      ],
      compute: function (v, s) {
        if (!has(v.og) || !has(v.vol) || !has(v.grain) || !has(v.ppg)) return null;
        var pts = (num(v.og) - 1) * 1000, volGal = s.toL(v.vol) / 3.785411784, lb = num(v.grain) * 2.2046226;
        if (pts <= 0 || lb <= 0 || num(v.ppg) <= 0) return { error: 'Enter a gravity above 1.000 and a grain weight.' };
        var eff = (pts * volGal) / (lb * num(v.ppg)) * 100;
        return { main: { value: d(eff, 1), unit: '%', label: 'Brewhouse efficiency' },
          note: 'Total sugar captured from grain into the fermenter.' };
      },
    },
    {
      id: 'beer-mashtun',
      name: 'Mash Tun Calibration Calculator',
      desc: 'Measure the thermal mass of your mash tun.',
      group: 'mash', tag: 'Advanced',
      need: 'Add water of a known temperature to your empty tun and record the temperatures.',
      accuracy: 'Insulate the tun and read the equilibrium temperature after about 5 minutes.',
      next: [{ t: 'beer-strike', label: 'Calculate strike temperature' }],
      fields: [
        { key: 'tun', label: 'Mash tun starting temperature', type: 'number', dim: 'temp', default: '20' },
        { key: 'vol', label: 'Volume of water added', type: 'number', dim: 'vol', default: '15' },
        { key: 'tw', label: 'Water temperature added', type: 'number', dim: 'temp', default: '75' },
        { key: 'teq', label: 'Temperature after 5 minutes', type: 'number', dim: 'temp', default: '72' },
      ],
      compute: function (v, s) {
        if (!has(v.tun) || !has(v.vol) || !has(v.tw) || !has(v.teq)) return null;
        var Ttun = s.toC(v.tun), Tw = s.toC(v.tw), Teq = s.toC(v.teq), Vw = s.toL(v.vol);
        if (Teq <= Ttun || Tw <= Teq) return { error: 'The 5-minute temperature must sit between the tun and water temperatures.' };
        var cap = Vw * (Tw - Teq) / (Teq - Ttun);
        var cv = s.vol(cap);
        return { main: { value: cv.value, unit: cv.unit, label: 'Tun thermal mass (water-equivalent)' },
          note: 'Add this as extra water when calculating your strike temperature.' };
      },
    },

    /* ---------- Convert & check ---------- */
    {
      id: 'beer-gravconv',
      name: 'Gravity Converter',
      desc: 'Convert between Specific Gravity, Plato and Brix.',
      group: 'convert', tag: 'Handy',
      need: 'A gravity value in SG, Plato or Brix.',
      next: [{ t: 'beer-colourconv', label: 'Convert colour units' }],
      fields: [
        { key: 'from', label: 'Input unit', type: 'select', default: 'sg',
          options: [{ value: 'sg', label: 'Specific Gravity' }, { value: 'plato', label: 'Plato (°P)' }, { value: 'brix', label: 'Brix (°Bx)' }] },
        { key: 'value', label: 'Value', type: 'number', unit: '', default: '1.050', step: '0.001' },
      ],
      compute: function (v, s) {
        if (!has(v.value)) return null;
        var sgToP = function (sg) { return -616.868 + 1111.14 * sg - 630.272 * sg * sg + 135.997 * sg * sg * sg; };
        var pToSg = function (p) { return 1 + p / (258.6 - (p / 258.2) * 227.1); };
        var sgToBrix = function (sg) { return (((182.4601 * sg - 775.6821) * sg + 1262.7794) * sg - 669.5622); };
        var x = num(v.value), sg = v.from === 'sg' ? x : pToSg(x);
        if (sg < 0.99 || sg > 1.2) return { error: 'Enter a realistic gravity value.' };
        return { main: { value: d(sg, 3), unit: 'SG', label: 'Specific Gravity' },
          extra: [{ value: d(sgToP(sg), 1), unit: '°P', label: 'Plato' }, { value: d(sgToBrix(sg), 1), unit: '°Bx', label: 'Brix' }],
          note: 'Brix and Plato are nearly identical; small differences are expected.' };
      },
    },
    {
      id: 'beer-colourconv',
      name: 'Colour Converter',
      desc: 'Convert between EBC, SRM and Lovibond colour scales.',
      group: 'convert', tag: 'Handy',
      need: 'A colour value in EBC, SRM or Lovibond.',
      next: [{ t: 'beer-gravconv', label: 'Convert gravity units' }],
      fields: [
        { key: 'from', label: 'Input scale', type: 'select', default: 'ebc',
          options: [{ value: 'ebc', label: 'EBC' }, { value: 'srm', label: 'SRM' }, { value: 'lov', label: 'Lovibond (°L)' }] },
        { key: 'value', label: 'Value', type: 'number', unit: '', default: '40' },
      ],
      compute: function (v, s) {
        if (!has(v.value)) return null;
        var x = num(v.value), srm = v.from === 'ebc' ? x / 1.97 : v.from === 'srm' ? x : 1.3546 * x - 0.76;
        if (srm < 0) return { error: 'Enter a valid colour value.' };
        return { main: { value: d(srm, 1), unit: 'SRM', label: 'SRM' },
          extra: [{ value: d(srm * 1.97, 1), unit: 'EBC', label: 'EBC' }, { value: d((srm + 0.76) / 1.3546, 1), unit: '°L', label: 'Lovibond' }],
          note: 'Standard beer colour-scale conversions.' };
      },
    },
    {
      id: 'beer-gravadj',
      name: 'Gravity Adjustment Calculator',
      desc: 'Calculate the boil-off, water or malt extract needed to hit a target gravity.',
      group: 'convert',
      need: 'Your wort volume, current gravity, desired gravity and the action to take.',
      accuracy: 'This is an estimate. Re-check gravity after adjusting.',
      next: [{ t: 'beer-abv', label: 'Calculate ABV' }],
      fields: [
        { key: 'vol', label: 'Wort volume', type: 'number', dim: 'vol', default: '20' },
        { key: 'cur', label: 'Current gravity', type: 'number', unit: 'SG', default: '1.040', step: '0.001' },
        { key: 'des', label: 'Desired gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'action', label: 'Action', type: 'select', default: 'boil',
          options: [{ value: 'boil', label: 'Boil off (raise gravity)' }, { value: 'dme', label: 'Add dry malt extract' }, { value: 'water', label: 'Add water (lower gravity)' }] },
      ],
      compute: function (v, s) {
        if (!has(v.vol) || !has(v.cur) || !has(v.des)) return null;
        var cp = (num(v.cur) - 1) * 1000, dp = (num(v.des) - 1) * 1000, L = s.toL(v.vol);
        if (cp <= 0 || dp <= 0) return { error: 'Enter gravities above 1.000.' };
        if (v.action === 'boil') {
          if (dp <= cp) return { error: 'Desired gravity must be higher than current to boil off.' };
          var vF = L * cp / dp, off = s.vol(L - vF), fv = s.vol(vF);
          return { main: { value: off.value, unit: off.unit, label: 'Volume to boil off' },
            extra: [{ value: fv.value, unit: fv.unit, label: 'Final volume' }],
            note: 'Boil down to the final volume to concentrate the wort.' };
        } else if (v.action === 'water') {
          if (dp >= cp) return { error: 'Desired gravity must be lower than current to add water.' };
          var vN = L * cp / dp, add = s.vol(vN - L), nv = s.vol(vN);
          return { main: { value: add.value, unit: add.unit, label: 'Water to add' },
            extra: [{ value: nv.value, unit: nv.unit, label: 'Final volume' }],
            note: 'Dilutes gravity, flavour and bitterness together.' };
        }
        if (dp <= cp) return { error: 'Desired gravity must be higher than current to add extract.' };
        var w = s.wt((dp - cp) * L / 375.5 * 1000);
        return { main: { value: w.value, unit: w.unit, label: 'Dry malt extract to add' },
          note: 'Dissolve and stir in, then re-check gravity.' };
      },
    },
    {
      id: 'beer-atten',
      name: 'Apparent / Real Attenuation Calculator',
      desc: 'Calculate how much of the sugar your yeast fermented.',
      group: 'convert',
      need: 'Your original and final gravity.',
      accuracy: 'Real attenuation corrects for alcohol being lighter than water.',
      next: [{ t: 'beer-abv', label: 'Calculate ABV' }],
      fields: [
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'fg', label: 'Final Gravity', type: 'number', unit: 'SG', default: '1.010', step: '0.001' },
      ],
      compute: function (v, s) {
        if (!has(v.og) || !has(v.fg)) return null;
        var og = num(v.og), fg = num(v.fg);
        if (og <= 1 || fg <= 0.98 || fg > og) return { error: 'Enter a valid OG and FG (FG below OG).' };
        var ogp = (og - 1) * 1000, fgp = (fg - 1) * 1000, aa = (ogp - fgp) / ogp * 100, ra = 0.8192 * aa;
        return { main: { value: d(aa, 1), unit: '%', label: 'Apparent attenuation' },
          extra: [{ value: d(ra, 1), unit: '%', label: 'Real attenuation' }, { value: d(aa - ra, 1), unit: '%', label: 'Difference' }],
          note: 'Apparent reads high because alcohol is lighter than water.' };
      },
    },
    {
      id: 'beer-calories',
      name: 'Calories in ABV Calculator',
      desc: 'Estimate the calories in a serving of your beer.',
      group: 'convert',
      need: 'Your original gravity, final gravity and serving size.',
      accuracy: 'A standard estimate — actual figures vary with recipe and ingredients.',
      next: [{ t: 'beer-abv', label: 'Calculate ABV' }],
      fields: [
        { key: 'og', label: 'Original Gravity', type: 'number', unit: 'SG', default: '1.050', step: '0.001' },
        { key: 'fg', label: 'Final Gravity', type: 'number', unit: 'SG', default: '1.010', step: '0.001' },
        { key: 'serving', label: 'Serving size', type: 'number', unit: 'mL', default: '330' },
      ],
      compute: function (v, s) {
        if (!has(v.og) || !has(v.fg)) return null;
        var og = num(v.og), fg = num(v.fg);
        if (og <= 1 || fg <= 0.98 || fg > og) return { error: 'Enter a valid OG and FG.' };
        var sgToP = function (sg) { return -616.868 + 1111.14 * sg - 630.272 * sg * sg + 135.997 * sg * sg * sg; };
        var abv = (og - fg) * 131.25, abw = 0.79 * abv / fg, re = 0.1808 * sgToP(og) + 0.8192 * sgToP(fg);
        var mL = has(v.serving) ? num(v.serving) : 330;
        var cal = Math.max((6.9 * abw + 4.0 * (re - 0.1)) * fg * (mL / 100), 0);
        return { main: { value: d(cal, 0), unit: 'kcal', label: 'Calories per serving' },
          extra: [{ value: d(cal / (mL / 100), 0), unit: 'kcal/100mL', label: 'Energy density' }],
          note: 'Estimated for a ' + d(mL, 0) + ' mL serving.' };
      },
    },
    {
      id: 'beer-hopage',
      name: 'Hop Age Calculator',
      desc: 'Estimate the current alpha acid of hops after storage.',
      group: 'convert',
      need: 'The original alpha acid, hop form and how long they have been stored.',
      accuracy: 'A storage estimate — real losses depend on temperature, packaging and oxygen exposure.',
      next: [{ t: 'beer-ibu', label: 'Calculate bitterness' }, { h: '/collections/beer-brewing-ingredients', label: 'Shop hops & ingredients' }],
      fields: [
        { key: 'aa', label: 'Original alpha acid', type: 'number', unit: '%', default: '10', step: '0.1' },
        { key: 'form', label: 'Hop form', type: 'select', default: 'pellet',
          options: [{ value: 'pellet', label: 'Pellet' }, { value: 'whole', label: 'Whole / leaf' }] },
        { key: 'months', label: 'Months stored', type: 'number', unit: 'months', default: '6' },
      ],
      compute: function (v, s) {
        if (!has(v.aa) || !has(v.months)) return null;
        var k = { pellet: 2.885e-4, whole: 9.77e-4 }[v.form], days = num(v.months) * 30.4368;
        var cur = num(v.aa) * Math.exp(-k * days);
        return { main: { value: d(cur, 1), unit: '% AA', label: 'Estimated current alpha acid' },
          extra: [{ value: d((1 - cur / num(v.aa)) * 100, 0), unit: '% lost', label: 'Alpha acid lost' }],
          note: 'Assumes cool, sealed storage. Increase your hop weight to compensate.' };
      },
    },
  ];

  var CFG = {
    page: 'beer',
    units: 'metric',
    // Group order defines the continuous 01–27 numbering (Plan → Mash → Measure → Convert → Package)
    groups: [
      { id: 'plan', anchor: 'grp-plan', title: 'Plan your batch', learn: [
        { title: 'How to take a hydrometer reading', kicker: 'Guide', href: '/blogs/all/how-to-take-a-hydrometer-reading-for-home-brewing' },
        { title: 'Pitching yeast: how much you need', kicker: 'Guide', href: '/blogs/all/pitching-yeast-how-much-yeast-do-you-need-for-beer' },
      ] },
      { id: 'mash', anchor: 'grp-mash', title: 'Mash & all-grain', learn: [
        { title: 'Shop all-grain brewing systems', kicker: 'Shop', href: '/collections/all-grain-brewing-systems' },
        { title: 'Mashing & testing equipment', kicker: 'Shop', href: '/collections/hydrometers-refractometers' },
      ] },
      { id: 'measure', anchor: 'grp-measure', title: 'Measure and adjust', learn: [
        { title: 'Reading a refractometer', kicker: 'Guide', href: '/blogs/all/how-to-read-a-refractometer-when-brewing-beer' },
        { title: 'Understanding IBU and bitterness', kicker: 'Guide', href: '/blogs/all/understanding-ibu-and-bitterness-in-beer' },
        { title: 'Adjusting a batch with water', kicker: 'Guide', href: '/blogs/all/adjusting-a-beer-batch-with-water' },
      ] },
      { id: 'convert', anchor: 'grp-convert', title: 'Convert & check', learn: [
        { title: 'Browse beer brewing guides', kicker: 'Guide', href: '/collections/beer-brewing' },
        { title: 'Measuring & testing equipment', kicker: 'Shop', href: '/collections/hydrometers-refractometers' },
      ] },
      { id: 'package', anchor: 'grp-package', title: 'Carbonate and package', learn: [
        { title: 'Understanding carbonation levels', kicker: 'Guide', href: '/blogs/all/understanding-carbonation-levels-in-beer' },
        { title: 'How to bottle beer', kicker: 'Guide', href: '/blogs/all/how-to-bottle-beer-at-home' },
      ] },
    ],
    // "What are you trying to do?" quick-nav chips (chipDefs)
    chips: [
      { label: 'Measure alcohol', id: 'beer-abv' },
      { label: 'All-grain mash', id: 'beer-mashsparge' },
      { label: 'Adjust a batch', id: 'beer-gravadj' },
      { label: 'Convert units', id: 'beer-gravconv' },
      { label: 'Carbonate beer', id: 'beer-priming' },
      { label: 'Bottle and package', id: 'beer-bottle' },
    ],
    footer: {
      responsible: 'Calculator results are estimates, particularly for homemade beverages. Enjoy what you make, share responsibly and never drink and drive.',
      related: [
        { label: 'Browse beer brewing guides', href: '/collections/beer-brewing' },
        { label: 'Explore bottling supplies', href: '/collections/bottle-closures-packaging' },
        { label: 'View measuring and testing equipment', href: '/collections/hydrometers-refractometers' },
      ],
    },
    calculators: beer,
  };

  if (window.TABCalc && window.TABCalc.register) window.TABCalc.register('beer', CFG);
  else { window.TABCalc = window.TABCalc || {}; (window.TABCalc._pending = window.TABCalc._pending || []).push(['beer', CFG]); }
})();
